import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCotacaoOnline, PerdaTotal } from '../../../api/orcamentoCiliaApi';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/dashboard-base.css';
import '../pages/OrcamentoCiliaDetail.css';

/**
 * Perda total (at-15, decisão do Victor de 20/10/2026): a cotação contra 70% da FIPE do veículo,
 * em duas cestas — a de menor e a de maior preço de cada peça, mais os serviços.
 *
 * Usa a MESMA consulta da seção de cotação online (mesma chave), então se atualiza sozinha a cada
 * pesquisa que termina, sem custar uma requisição a mais.
 *
 * Falha de carga NUNCA vira "não é perda total" (at-60): a tela diz que não conseguiu verificar.
 */
const moeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v: number | null) => (v == null ? '-' : `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

const AvisoPerdaTotal: React.FC<{ orcamentoId: string }> = ({ orcamentoId }) => {
  const consulta = useQuery({
    queryKey: ['orcamento-cilia', 'cotacao-online', orcamentoId],
    queryFn: () => fetchCotacaoOnline(orcamentoId),
    enabled: !!orcamentoId,
    refetchInterval: (q) => (q.state.data?.itens.some((i) => i.emCurso) ? 10_000 : false),
  });

  if (consulta.isError) {
    return (
      <p className="erro-de-tela" role="alert">
        Não foi possível verificar a perda total desta requisição. Recarregue a página; se persistir, acione a TI.
      </p>
    );
  }
  const pt = consulta.data?.perdaTotal;
  // Sem cálculo ainda: ninguém usou o "Cotar peças online" (é lá que a perda total começa).
  if (!pt) return null;

  return <Faixa pt={pt} />;
};

/** Selo e frase de cada situação. A cor segue a gravidade, e a frase diz o que ela significa. */
const SITUACAO: Record<PerdaTotal['situacao'], { selo: string; cor: string; frase: (limite: string) => string }> = {
  PT_NAS_DUAS: {
    selo: 'Perda total',
    cor: 'badge-vermelho',
    frase: (l) => `A cotação atinge ${l} ou mais da FIPE mesmo na cesta de menor valor.`,
  },
  PT_SO_NA_MAIOR: {
    selo: 'Perda total só na cesta de maior valor',
    cor: 'badge-laranja',
    frase: (l) => `A cesta de maior valor atinge ${l} da FIPE; a de menor valor, não. Vale conferir de onde vêm os preços altos.`,
  },
  ABAIXO: {
    selo: 'Abaixo do limite',
    cor: 'badge-verde',
    frase: (l) => `As duas cestas ficam abaixo de ${l} da FIPE.`,
  },
  SEM_PRECO: {
    selo: 'Aguardando preços',
    cor: 'badge-cinza',
    frase: () => 'Ainda não há preço online de nenhuma peça para comparar com a FIPE.',
  },
  SEM_FIPE: {
    selo: 'Não verificada',
    cor: 'badge-cinza',
    frase: () => 'O valor FIPE do veículo não está disponível.',
  },
};

/**
 * Uma cesta: o percentual da FIPE em destaque, o valor em reais e um medidor com a marca do limite.
 * O medidor vai até 100% da FIPE; acima disso fica cheio e o número diz quanto passou.
 */
const Cesta: React.FC<{ titulo: string; valor: number; percentual: number | null; limite: number }> = ({
  titulo,
  valor,
  percentual,
  limite,
}) => {
  const atinge = percentual != null && percentual >= limite;
  const largura = percentual == null ? 0 : Math.min(100, Math.max(0, percentual * 100));
  return (
    <div className="do-kpi">
      <span className="do-kpi-rotulo">{titulo}</span>
      <span className="do-kpi-valor" style={{ color: atinge ? 'var(--tema-texto-vermelho, #c62828)' : undefined }}>
        {pct(percentual)} <span className="do-kpi-apoio">da FIPE</span>
      </span>
      <span className="do-kpi-apoio">{moeda(valor)}</span>
      <div style={{ position: 'relative' }}>
        <div className="do-medidor" style={{ background: atinge ? 'var(--tema-tinta-vermelho, #f9d6d6)' : undefined }}>
          <div className="do-medidor-preenchimento" style={{ width: `${largura}%`, background: atinge ? '#c62828' : undefined }} />
        </div>
        {/* A marca do limite: é contra ela que a cesta é lida. */}
        <div
          title={`Limite de perda total: ${pct(limite)} da FIPE`}
          style={{ position: 'absolute', left: `${limite * 100}%`, top: 0, bottom: -4, width: 2, background: '#0b0b0b' }}
        />
      </div>
    </div>
  );
};

const Faixa: React.FC<{ pt: PerdaTotal }> = ({ pt }) => {
  const limite = pct(pt.limite);
  const fipe = pt.fipe != null ? moeda(pt.fipe) : null;
  const sit = SITUACAO[pt.situacao] ?? SITUACAO.SEM_FIPE;

  const faltam: string[] = [];
  if (pt.pecasPesquisando > 0) faltam.push(`${pt.pecasPesquisando} ainda em pesquisa`);
  if (pt.pecasSemPreco > 0) faltam.push(`${pt.pecasSemPreco} sem preço encontrado online`);
  const notaPecas = faltam.length
    ? `Peças fora da conta: ${faltam.join(' e ')}. Com elas, o percentual real pode ser maior.`
    : null;

  const temCestas = pt.situacao !== 'SEM_FIPE' && pt.situacao !== 'SEM_PRECO';
  const frase = pt.situacao === 'SEM_FIPE' ? pt.fipeMotivo ?? sit.frase(limite) : sit.frase(limite);

  return (
    <section
      className="cilia-solicitacao-card"
      role={pt.situacao === 'PT_NAS_DUAS' ? 'alert' : 'status'}
      aria-label="Perda total"
      style={{ marginBottom: 20 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h4 className="do-card-titulo" style={{ margin: 0 }}>
          Perda total
        </h4>
        <span className={`cilia-badge ${sit.cor}`}>{sit.selo}</span>
        {fipe && (
          <span className="do-kpi-apoio" style={{ marginLeft: 'auto' }}>
            Valor FIPE: <strong style={{ color: 'var(--tema-texto, #0b0b0b)' }}>{fipe}</strong>
            {pt.fipeMesReferencia ? ` (${pt.fipeMesReferencia})` : ''}
          </span>
        )}
      </div>
      <p className="do-card-descricao" style={{ marginBottom: temCestas ? 14 : 0 }}>
        {frase} É perda total quando a cotação chega a {limite} do valor FIPE.
      </p>
      {temCestas && (
        <div className="do-kpis" style={{ marginBottom: 0 }}>
          <Cesta titulo="Cesta de menor valor" valor={pt.cestaMenor} percentual={pt.percentualMenor} limite={pt.limite} />
          <Cesta titulo="Cesta de maior valor" valor={pt.cestaMaior} percentual={pt.percentualMaior} limite={pt.limite} />
        </div>
      )}
      {(temCestas || pt.situacao === 'SEM_PRECO') && (
        <p className="do-kpi-hint" style={{ marginBottom: 0 }}>
          Serviços incluídos nas duas cestas: {moeda(pt.servicos)}.{notaPecas ? ` ${notaPecas}` : ''}
        </p>
      )}
    </section>
  );
};

export default AvisoPerdaTotal;
